import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Sparkles, Wand2, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
const absolute = (url) => (url && url.startsWith('http') ? url : `${BACKEND_URL}${url || ''}`);

/**
 * Generate an image with AI: GPT writes the brief, Nano Banana draws it.
 * The prompt stays with the image so it can be nudged rather than redone.
 */
export const AIImagePanel = ({ context = {}, onUse }) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [alt, setAlt] = useState('');
  const [image, setImage] = useState(null);
  const [tweak, setTweak] = useState('');
  const [busy, setBusy] = useState('');
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ai-image/recent`, { headers: auth() });
      setRecent(res.data.items || []);
    } catch { /* the panel still works without the history strip */ }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const fail = (e, title) =>
    toast({ title, description: e.response?.data?.detail || e.message, variant: 'destructive' });

  const writeBrief = async () => {
    setBusy('brief');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ai-image/prompt`, {
        title: context.title || '', context: context.context || '', kind: context.kind || 'post',
      }, { headers: auth() });
      setPrompt(res.data.prompt);
      setAlt(res.data.alt || '');
    } catch (e) { fail(e, 'Could not write a prompt'); } finally { setBusy(''); }
  };

  const generate = async () => {
    setBusy('generate');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ai-image/generate`, {
        prompt, alt, kind: context.kind || 'post', title: context.title || '',
      }, { headers: auth() });
      setImage(res.data);
      setAlt(res.data.alt || alt);
      loadRecent();
    } catch (e) { fail(e, 'Image generation failed'); } finally { setBusy(''); }
  };

  const nudge = async () => {
    if (!image) return;
    setBusy('nudge');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/ai-image/${image.id}/regenerate`,
        { tweak }, { headers: auth() });
      setImage(res.data);
      setTweak('');
      loadRecent();
    } catch (e) { fail(e, 'Could not regenerate'); } finally { setBusy(''); }
  };

  const working = Boolean(busy);
  const hasContext = Boolean((context.title || '').trim() || (context.context || '').trim());

  return (
    <div className="p-5 space-y-5" data-testid="ai-image-panel">
      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Describe the photograph
          </label>
          <Button type="button" variant="outline" size="sm" onClick={writeBrief}
                  disabled={working || !hasContext}
                  title={hasContext ? 'GPT writes the brief from what you are working on'
                                    : 'Add a title first so there is something to work from'}
                  data-testid="ai-image-write-brief">
            {busy === 'brief' ? <Loader2 size={13} className="mr-2 animate-spin" />
                              : <Wand2 size={13} className="mr-2" />}
            Write it for me
          </Button>
        </div>
        <textarea
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A wet January morning on Paisley High Street, low grey cloud over the sandstone frontages, pavements reflecting shop lights..."
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
          data-testid="ai-image-prompt"
        />
        <div className="flex items-center gap-3 mt-3">
          <Button type="button" onClick={generate} disabled={working || prompt.trim().length < 10}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="ai-image-generate">
            {busy === 'generate' ? <Loader2 size={14} className="mr-2 animate-spin" />
                                 : <Sparkles size={14} className="mr-2" />}
            {busy === 'generate' ? 'Drawing...' : 'Generate image'}
          </Button>
          <p className="text-xs text-slate-500">
            {hasContext ? 'Takes around 10-20 seconds.'
                        : 'Add a title to the post or event and AI can write the description for you.'}
          </p>
        </div>
      </div>

      {image && (
        <div className="border-t border-slate-200 pt-5" data-testid="ai-image-result">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,320px)_1fr] items-start">
            <img src={absolute(image.url)} alt={alt}
                 className="w-full rounded-lg border border-slate-200" />
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Alt text
                </label>
                <Input value={alt} onChange={(e) => setAlt(e.target.value)}
                       placeholder="Describe the photo for screen readers"
                       data-testid="ai-image-alt" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Not quite right? Nudge it
                </label>
                <div className="flex gap-2">
                  <Input value={tweak} onChange={(e) => setTweak(e.target.value)}
                         placeholder="More overcast, wider shot, fewer people"
                         data-testid="ai-image-tweak" />
                  <Button type="button" variant="outline" onClick={nudge} disabled={working}
                          data-testid="ai-image-regenerate">
                    {busy === 'nudge' ? <Loader2 size={14} className="animate-spin" />
                                      : <RefreshCw size={14} />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Keeps the same scene and applies your change.
                </p>
              </div>
              <Button type="button" onClick={() => onUse(absolute(image.url), alt)}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="ai-image-use">
                Use this image
              </Button>
            </div>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="border-t border-slate-200 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
            Generated earlier
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ai-image-recent">
            {recent.map((r) => (
              <button type="button" key={r.id} onClick={() => onUse(absolute(r.url), r.alt || '')}
                      title={r.ai_prompt}
                      className="shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 border-transparent hover:border-cyan-500 transition-colors"
                      data-testid={`ai-image-recent-${r.id}`}>
                <img src={absolute(r.url)} alt={r.alt || ''} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIImagePanel;
